import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db, hasFirebaseConfig } from "../firebaseClient";

const LOCAL_BUDGET_KEY = "budget_data_v2";
const BUDGET_COLLECTION = "budgetProfiles";
const BUDGET_DOC_ID = "main";

const getBudgetDocId = (userId) =>
  userId ? `user-${String(userId)}` : BUDGET_DOC_ID;

const getLocalBudgetKey = (userId) =>
  userId ? `${LOCAL_BUDGET_KEY}_${String(userId)}` : LOCAL_BUDGET_KEY;

const emptyBudgetState = () => ({
  budgetsByPeriod: {},
  paymentsByPeriod: {},
  budgetSourcesByPeriod: {},
  paydaysByMonth: {},
});

const normalizePayment = (entry) => {
  const id = String(
    entry?.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  );
  const name = String(entry?.name || "").trim();
  const amount = Number(entry?.amount);
  const isChecked = Boolean(entry?.isChecked);
  const recurring = Boolean(entry?.recurring);
  const recurringSourceId = entry?.recurringSourceId
    ? String(entry.recurringSourceId)
    : "";
  const createdAt = Number(entry?.createdAt) || Date.now();

  return {
    id,
    name,
    amount: Number.isFinite(amount) && amount >= 0 ? amount : 0,
    isChecked,
    recurring,
    recurringSourceId,
    createdAt,
  };
};

const parsePeriodKey = (periodKey) => {
  const nextKey = String(periodKey || "");
  const parenthesesMatch = /^(\d{4})-(\d{2})\((26-10|11-25)\)$/.exec(nextKey);

  if (parenthesesMatch) {
    return {
      year: Number(parenthesesMatch[1]),
      month: Number(parenthesesMatch[2]),
      cutoff: parenthesesMatch[3],
    };
  }

  const legacyMatch = /^(\d{4})-(\d{2})__(26-10|11-25)$/.exec(nextKey);
  if (legacyMatch) {
    return {
      year: Number(legacyMatch[1]),
      month: Number(legacyMatch[2]),
      cutoff: legacyMatch[3],
    };
  }

  return null;
};

const buildPeriodKey = (year, month, cutoff) =>
  `${year}-${String(month).padStart(2, "0")}(${cutoff})`;

const buildRecurringMarker = (entry) => {
  const source = String(entry?.recurringSourceId || entry?.id || "").trim();
  if (source) {
    return `source:${source}`;
  }

  const name = String(entry?.name || "")
    .trim()
    .toLowerCase();
  const amount = Number(entry?.amount);
  return `fallback:${name}::${Number.isFinite(amount) ? amount.toFixed(2) : "0.00"}`;
};

const normalizePaymentsByPeriod = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }

  const normalized = {};

  Object.entries(raw).forEach(([periodKey, entries]) => {
    if (!Array.isArray(entries)) {
      return;
    }

    normalized[periodKey] = entries
      .map(normalizePayment)
      .filter((entry) => entry.name.length > 0);
  });

  return normalized;
};

const normalizeBudgetsByPeriod = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }

  const normalized = {};

  Object.entries(raw).forEach(([periodKey, amount]) => {
    const value = Number(amount);
    normalized[periodKey] = Number.isFinite(value) && value >= 0 ? value : 0;
  });

  return normalized;
};

const normalizeBudgetSource = (entry) => {
  const id = String(
    entry?.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  );
  const type = String(entry?.type || "other")
    .trim()
    .toLowerCase();
  const name = String(entry?.name || "").trim();
  const amount = Number(entry?.amount);
  const createdAt = Number(entry?.createdAt) || Date.now();

  return {
    id,
    type: type || "other",
    name,
    amount: Number.isFinite(amount) && amount >= 0 ? amount : 0,
    createdAt,
  };
};

const normalizeBudgetSourcesByPeriod = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }

  const normalized = {};

  Object.entries(raw).forEach(([periodKey, entries]) => {
    if (!Array.isArray(entries)) {
      return;
    }

    normalized[periodKey] = entries
      .map(normalizeBudgetSource)
      .filter((entry) => entry.name.length > 0 || entry.amount > 0);
  });

  return normalized;
};

const normalizePaydaysByMonth = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }

  const normalized = {};

  Object.entries(raw).forEach(([monthKey, values]) => {
    if (!Array.isArray(values)) {
      return;
    }

    const unique = Array.from(
      new Set(
        values
          .map((entry) => String(entry || "").trim())
          .filter((entry) => /^\d{4}-\d{2}-\d{2}$/.test(entry)),
      ),
    );

    if (unique.length > 0) {
      normalized[monthKey] = unique;
    }
  });

  return normalized;
};

const normalizeBudgetState = (payload) => {
  if (!payload || typeof payload !== "object") {
    return emptyBudgetState();
  }

  // Legacy migration (v1 shape)
  if (
    ("monthlyBudget" in payload || "payments" in payload) &&
    !payload.budgetsByPeriod &&
    !payload.paymentsByPeriod
  ) {
    const fallbackPeriod = "legacy-monthly";
    const monthlyBudgetValue = Number(payload?.monthlyBudget);

    return {
      budgetsByPeriod: {
        [fallbackPeriod]:
          Number.isFinite(monthlyBudgetValue) && monthlyBudgetValue >= 0
            ? monthlyBudgetValue
            : 0,
      },
      paymentsByPeriod: {
        [fallbackPeriod]: Array.isArray(payload?.payments)
          ? payload.payments
              .map(normalizePayment)
              .filter((entry) => entry.name.length > 0)
          : [],
      },
    };
  }

  return {
    budgetsByPeriod: normalizeBudgetsByPeriod(payload.budgetsByPeriod),
    paymentsByPeriod: normalizePaymentsByPeriod(payload.paymentsByPeriod),
    budgetSourcesByPeriod: normalizeBudgetSourcesByPeriod(
      payload.budgetSourcesByPeriod,
    ),
    paydaysByMonth: normalizePaydaysByMonth(payload.paydaysByMonth),
  };
};

const readLocalBudgetState = (userId) => {
  try {
    const scopedKey = getLocalBudgetKey(userId);
    const raw = localStorage.getItem(scopedKey);

    if (!raw && userId) {
      // Fallback for legacy/global local data before auth was introduced.
      const legacyRaw = localStorage.getItem(LOCAL_BUDGET_KEY);
      if (legacyRaw) {
        return normalizeBudgetState(JSON.parse(legacyRaw));
      }
    }

    if (!raw) {
      return emptyBudgetState();
    }

    return normalizeBudgetState(JSON.parse(raw));
  } catch (error) {
    console.error("Failed to read local budget data", error);
    return emptyBudgetState();
  }
};

const writeLocalBudgetState = (state, userId) => {
  localStorage.setItem(getLocalBudgetKey(userId), JSON.stringify(state));
};

const writeCloudBudgetState = async (state, userId) => {
  const ref = doc(db, BUDGET_COLLECTION, getBudgetDocId(userId));

  await setDoc(
    ref,
    {
      ...state,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
};

const persistBudgetState = async (state, userId) => {
  const normalized = normalizeBudgetState(state);
  writeLocalBudgetState(normalized, userId);

  if (hasFirebaseConfig && db) {
    try {
      await writeCloudBudgetState(normalized, userId);
    } catch (error) {
      console.error("Failed to save budget data to Firestore", error);
    }
  }

  return normalized;
};

const ensurePeriodPayments = (state, periodKey) => {
  const payments = state.paymentsByPeriod[periodKey];
  return Array.isArray(payments) ? payments : [];
};

const ensurePeriodBudgetSources = (state, periodKey) => {
  const sources = state.budgetSourcesByPeriod[periodKey];
  return Array.isArray(sources) ? sources : [];
};

export const loadBudgetState = async (userId = null) => {
  if (hasFirebaseConfig && db) {
    try {
      const ref = doc(db, BUDGET_COLLECTION, getBudgetDocId(userId));
      const snap = await getDoc(ref);

      if (snap.exists()) {
        const normalized = normalizeBudgetState(snap.data());
        writeLocalBudgetState(normalized, userId);
        return normalized;
      }
    } catch (error) {
      console.error("Failed to load budget data from Firestore", error);
    }
  }

  return readLocalBudgetState(userId);
};

export const saveCutoffBudget = async (periodKey, value, userId = null) => {
  const nextBudget = Number(value);
  const current = await loadBudgetState(userId);

  return persistBudgetState(
    {
      ...current,
      budgetsByPeriod: {
        ...current.budgetsByPeriod,
        [periodKey]:
          Number.isFinite(nextBudget) && nextBudget >= 0 ? nextBudget : 0,
      },
    },
    userId,
  );
};

export const addCutoffPayment = async (
  periodKey,
  { name, amount, recurring },
  userId = null,
) => {
  const safeName = String(name || "").trim();
  const numericAmount = Number(amount);

  if (!safeName) {
    throw new Error("Payment name is required.");
  }

  if (!Number.isFinite(numericAmount) || numericAmount < 0) {
    throw new Error("Payment amount must be 0 or more.");
  }

  const current = await loadBudgetState(userId);
  const currentPeriodPayments = ensurePeriodPayments(current, periodKey);

  const nextPayment = normalizePayment({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: safeName,
    amount: numericAmount,
    recurring: Boolean(recurring),
    recurringSourceId: recurring ? undefined : "",
    isChecked: false,
    createdAt: Date.now(),
  });

  if (nextPayment.recurring) {
    nextPayment.recurringSourceId = nextPayment.id;
  }

  return persistBudgetState(
    {
      ...current,
      paymentsByPeriod: {
        ...current.paymentsByPeriod,
        [periodKey]: [nextPayment, ...currentPeriodPayments],
      },
    },
    userId,
  );
};

export const toggleCutoffPaymentChecked = async (
  periodKey,
  paymentId,
  isChecked,
  userId = null,
) => {
  const current = await loadBudgetState(userId);
  const currentPeriodPayments = ensurePeriodPayments(current, periodKey);

  const nextPayments = currentPeriodPayments.map((entry) =>
    entry.id === paymentId
      ? { ...entry, isChecked: Boolean(isChecked) }
      : entry,
  );

  return persistBudgetState(
    {
      ...current,
      paymentsByPeriod: {
        ...current.paymentsByPeriod,
        [periodKey]: nextPayments,
      },
    },
    userId,
  );
};

export const updateCutoffPayment = async (
  periodKey,
  paymentId,
  updates,
  userId = null,
) => {
  const current = await loadBudgetState(userId);
  const currentPeriodPayments = ensurePeriodPayments(current, periodKey);

  const nextName = String(updates?.name || "").trim();
  const nextAmount = Number(updates?.amount);

  if (!nextName) {
    throw new Error("Payment name is required.");
  }

  if (!Number.isFinite(nextAmount) || nextAmount < 0) {
    throw new Error("Payment amount must be 0 or more.");
  }

  const nextPayments = currentPeriodPayments.map((entry) =>
    entry.id === paymentId
      ? {
          ...entry,
          name: nextName,
          amount: nextAmount,
          recurring: updates?.recurring,
          recurringSourceId: updates?.recurring
            ? String(entry.recurringSourceId || entry.id)
            : "",
        }
      : entry,
  );

  return persistBudgetState(
    {
      ...current,
      paymentsByPeriod: {
        ...current.paymentsByPeriod,
        [periodKey]: nextPayments,
      },
    },
    userId,
  );
};

export const deleteCutoffPayment = async (
  periodKey,
  paymentId,
  userId = null,
) => {
  const current = await loadBudgetState(userId);
  const currentPeriodPayments = ensurePeriodPayments(current, periodKey);

  const nextPayments = currentPeriodPayments.filter(
    (entry) => entry.id !== paymentId,
  );

  return persistBudgetState(
    {
      ...current,
      paymentsByPeriod: {
        ...current.paymentsByPeriod,
        [periodKey]: nextPayments,
      },
    },
    userId,
  );
};

export const saveBudgetStateSnapshot = async (state, userId = null) =>
  persistBudgetState(
    {
      budgetsByPeriod: state?.budgetsByPeriod || {},
      paymentsByPeriod: state?.paymentsByPeriod || {},
      budgetSourcesByPeriod: state?.budgetSourcesByPeriod || {},
      paydaysByMonth: state?.paydaysByMonth || {},
    },
    userId,
  );

export const addBudgetSource = async (
  periodKey,
  { type, name, amount },
  userId = null,
) => {
  const safeName = String(name || "").trim();
  const safeType =
    String(type || "other")
      .trim()
      .toLowerCase() || "other";
  const numericAmount = Number(amount);

  if (!safeName) {
    throw new Error("Source name is required.");
  }

  if (!Number.isFinite(numericAmount) || numericAmount < 0) {
    throw new Error("Source amount must be 0 or more.");
  }

  const current = await loadBudgetState(userId);
  const currentSources = ensurePeriodBudgetSources(current, periodKey);
  const nextSource = normalizeBudgetSource({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type: safeType,
    name: safeName,
    amount: numericAmount,
    createdAt: Date.now(),
  });

  return persistBudgetState(
    {
      ...current,
      budgetSourcesByPeriod: {
        ...current.budgetSourcesByPeriod,
        [periodKey]: [nextSource, ...currentSources],
      },
    },
    userId,
  );
};

export const deleteBudgetSource = async (
  periodKey,
  sourceId,
  userId = null,
) => {
  const current = await loadBudgetState(userId);
  const currentSources = ensurePeriodBudgetSources(current, periodKey);

  const nextSources = currentSources.filter((entry) => entry.id !== sourceId);

  return persistBudgetState(
    {
      ...current,
      budgetSourcesByPeriod: {
        ...current.budgetSourcesByPeriod,
        [periodKey]: nextSources,
      },
    },
    userId,
  );
};

export const updateBudgetSource = async (
  periodKey,
  sourceId,
  updates,
  userId = null,
) => {
  const safeName = String(updates?.name || "").trim();
  const safeType =
    String(updates?.type || "other")
      .trim()
      .toLowerCase() || "other";
  const numericAmount = Number(updates?.amount);

  if (!safeName) {
    throw new Error("Source name is required.");
  }

  if (!Number.isFinite(numericAmount) || numericAmount < 0) {
    throw new Error("Source amount must be 0 or more.");
  }

  const current = await loadBudgetState(userId);
  const currentSources = ensurePeriodBudgetSources(current, periodKey);

  const nextSources = currentSources.map((entry) =>
    entry.id === sourceId
      ? {
          ...entry,
          type: safeType,
          name: safeName,
          amount: numericAmount,
        }
      : entry,
  );

  return persistBudgetState(
    {
      ...current,
      budgetSourcesByPeriod: {
        ...current.budgetSourcesByPeriod,
        [periodKey]: nextSources,
      },
    },
    userId,
  );
};

const toMonthKeyFromDateKey = (dateKey) => String(dateKey).slice(0, 7);

export const addPayday = async (dateKey, userId = null) => {
  const safeDateKey = String(dateKey || "").trim();

  if (!/^\d{4}-\d{2}-\d{2}$/.test(safeDateKey)) {
    throw new Error("Invalid payday date.");
  }

  const current = await loadBudgetState(userId);
  const monthKey = toMonthKeyFromDateKey(safeDateKey);
  const currentMonthPaydays = Array.isArray(current.paydaysByMonth?.[monthKey])
    ? current.paydaysByMonth[monthKey]
    : [];

  if (currentMonthPaydays.includes(safeDateKey)) {
    return current;
  }

  return persistBudgetState(
    {
      ...current,
      paydaysByMonth: {
        ...current.paydaysByMonth,
        [monthKey]: [...currentMonthPaydays, safeDateKey],
      },
    },
    userId,
  );
};

export const removePayday = async (dateKey, userId = null) => {
  const safeDateKey = String(dateKey || "").trim();

  if (!/^\d{4}-\d{2}-\d{2}$/.test(safeDateKey)) {
    throw new Error("Invalid payday date.");
  }

  const current = await loadBudgetState(userId);
  const monthKey = toMonthKeyFromDateKey(safeDateKey);
  const currentMonthPaydays = Array.isArray(current.paydaysByMonth?.[monthKey])
    ? current.paydaysByMonth[monthKey]
    : [];

  const nextMonthPaydays = currentMonthPaydays.filter(
    (entry) => entry !== safeDateKey,
  );

  const nextPaydaysByMonth = {
    ...current.paydaysByMonth,
    [monthKey]: nextMonthPaydays,
  };

  if (nextMonthPaydays.length === 0) {
    delete nextPaydaysByMonth[monthKey];
  }

  return persistBudgetState(
    {
      ...current,
      paydaysByMonth: nextPaydaysByMonth,
    },
    userId,
  );
};

export const ensureRecurringPaymentsForPeriod = async (
  periodKey,
  userId = null,
) => {
  const parsed = parsePeriodKey(periodKey);
  const current = await loadBudgetState(userId);

  if (!parsed) {
    return current;
  }

  let prevYear = parsed.year;
  let prevMonth = parsed.month - 1;

  if (prevMonth < 1) {
    prevMonth = 12;
    prevYear -= 1;
  }

  const prevPeriodKey = buildPeriodKey(prevYear, prevMonth, parsed.cutoff);
  const prevPayments = ensurePeriodPayments(current, prevPeriodKey);
  const currentPayments = ensurePeriodPayments(current, periodKey);

  const recurringTemplates = prevPayments.filter((entry) => entry.recurring);
  if (recurringTemplates.length === 0) {
    return current;
  }

  const existingMarkers = new Set(
    currentPayments
      .filter((entry) => entry.recurring)
      .map(buildRecurringMarker),
  );

  const nextRecurring = recurringTemplates
    .filter((entry) => !existingMarkers.has(buildRecurringMarker(entry)))
    .map((entry) => {
      const sourceId = String(entry.recurringSourceId || entry.id);
      return normalizePayment({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: entry.name,
        amount: entry.amount,
        recurring: true,
        recurringSourceId: sourceId,
        isChecked: false,
        createdAt: Date.now(),
      });
    });

  if (nextRecurring.length === 0) {
    return current;
  }

  return persistBudgetState(
    {
      ...current,
      paymentsByPeriod: {
        ...current.paymentsByPeriod,
        [periodKey]: [...nextRecurring, ...currentPayments],
      },
    },
    userId,
  );
};
